import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { getLegalPage } from '../utils/api';

const TermsOfUse = () => {
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const res = await getLegalPage('terms_of_use');
        const data = res.data || {};
        // Only use CMS content if it has actual content
        if (data.content && data.content.trim()) {
          setCmsContent(data);
        }
      } catch (error) {
        console.error('Failed to load Terms of Use:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818]"></div>
      </div>
    );
  }

  // If CMS content exists, render it
  if (cmsContent && cmsContent.content) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="py-12 bg-[#F9F9F7]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {cmsContent.title || 'Terms of Use'}
            </h1>
            {cmsContent.last_updated_date && (
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Last updated: {cmsContent.last_updated_date}
              </p>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: cmsContent.content }} />
          </div>
        </div>
      </div>
    );
  }

  // Default static content (original page)
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Terms of Use
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Last updated: December 8, 2025
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-700">
                By accessing and using the <span className="text-[#8B1818] font-semibold">Lex</span>DoRight website and services, you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Description of Services</h2>
              <p className="text-slate-700">
                <span className="text-[#8B1818] font-semibold">Lex</span>DoRight provides legal resources, intelligent tools, consultation booking services, educational content, and related products. Our services are designed for informational and educational purposes and do not constitute legal advice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. User Accounts</h2>
              <p className="text-slate-700">
                To access certain features, you may need to create an account. You are responsible for:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and complete information</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Consultation Services</h2>
              <p className="text-slate-700">
                Our consultation booking service connects you with legal professionals. Please note:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Consultations are scheduled based on availability</li>
                <li>Payment must be completed before confirmation</li>
                <li>Cancellations and refunds are subject to our cancellation policy</li>
                <li>Consultations are conducted via Viber at the scheduled time</li>
              </ul>
              
              <h3 className="text-xl font-bold text-slate-900 mt-6 mb-3">Confidentiality and Recording Policy</h3>
              <p className="text-slate-700 mb-3">
                All consultations between <span className="text-[#8B1818] font-semibold">Lex</span>DoRight legal professionals and clients are strictly confidential and protected by lawyer-client privilege. By booking a consultation, you agree to the following:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li><strong>No Recording:</strong> Audio or video recording of consultations by any party is strictly prohibited unless explicitly agreed upon in writing by all participants prior to the session</li>
                <li><strong>Confidentiality:</strong> All information shared during consultations shall be treated as confidential and shall not be disclosed to third parties without proper authorization</li>
                <li><strong>Lawyer-Client Privilege:</strong> Communications made in the course of the professional relationship are privileged and protected under the Rules of Court and the Code of Professional Responsibility</li>
                <li><strong>Data Protection:</strong> Any notes, documents, or information exchanged during consultations are handled in accordance with the Data Privacy Act of 2012 (Republic Act No. 10173)</li>
                <li><strong>Breach of Confidentiality:</strong> Any unauthorized recording or disclosure of consultation contents may result in termination of services and may be subject to legal action</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Prohibited Conduct</h2>
              <p className="text-slate-700">You agree not to:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Use the service for any unlawful purpose</li>
                <li>Post false, misleading, or defamatory content</li>
                <li>Interfere with the proper functioning of the website</li>
                <li>Attempt to gain unauthorized access to any systems</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Disclaimer</h2>
              <p className="text-slate-700">
                The information provided on <span className="text-[#8B1818] font-semibold">Lex</span>DoRight is for general informational purposes only and does not constitute legal advice. We do not guarantee the accuracy, completeness, or usefulness of any information. Always consult a qualified legal professional for specific legal matters.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-slate-700">
                <span className="text-[#8B1818] font-semibold">Lex</span>DoRight shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from your use of our services or reliance on any information provided.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Changes to Terms</h2>
              <p className="text-slate-700">
                We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Contact Us</h2>
              <p className="text-slate-700">
                If you have questions about these Terms of Use, please contact us at <a href="mailto:admin@lexdoright.com" className="text-[#8B1818] hover:underline">admin@lexdoright.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
